import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent } from 'react'
import { api } from '../lib/api.js'
import { PageHeader } from '../components/PageHeader.js'
import { Card, CardBody, CardHeader, CardFooter } from '../components/Card.js'
import { Button } from '../components/Button.js'
import { Field, Input, Select, Checkbox } from '../components/Field.js'

export const Route = createFileRoute('/_app/settings/payments')({
  component: BillingSettingsPage,
})

interface BillingSettings {
  surchargeMode: 'add' | 'absorb'
  settlementAccountHolderName: string | null
  settlementAccountNumber: string | null
  settlementIfsc: string | null
  gstin: string | null
  paymentRequestWhatsapp: boolean
  paymentRequestEmail: boolean
}

function BillingSettingsPage() {
  const qc = useQueryClient()

  const settings = useQuery({
    queryKey: ['billing-settings'],
    queryFn: async () => {
      const res = await api<BillingSettings>('/api/v1/academy/billing-settings')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const [form, setForm] = useState<BillingSettings>({
    surchargeMode: 'add',
    settlementAccountHolderName: '',
    settlementAccountNumber: '',
    settlementIfsc: '',
    gstin: '',
    paymentRequestWhatsapp: true,
    paymentRequestEmail: true,
  })
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    if (settings.data) {
      setForm({
        surchargeMode: settings.data.surchargeMode,
        settlementAccountHolderName: settings.data.settlementAccountHolderName ?? '',
        settlementAccountNumber: settings.data.settlementAccountNumber ?? '',
        settlementIfsc: settings.data.settlementIfsc ?? '',
        gstin: settings.data.gstin ?? '',
        paymentRequestWhatsapp: settings.data.paymentRequestWhatsapp,
        paymentRequestEmail: settings.data.paymentRequestEmail,
      })
    }
  }, [settings.data])

  const save = useMutation({
    mutationFn: async () => {
      // Send empty optional fields as null so the API clears them cleanly.
      const clean = (v: string | null | undefined, upper = false) => {
        const t = (v ?? '').trim()
        if (!t) return null
        return upper ? t.toUpperCase() : t
      }
      const res = await api<BillingSettings>('/api/v1/academy/billing-settings', {
        method: 'PUT',
        body: JSON.stringify({
          surchargeMode: form.surchargeMode,
          settlementAccountHolderName: clean(form.settlementAccountHolderName),
          settlementAccountNumber: clean(form.settlementAccountNumber),
          settlementIfsc: clean(form.settlementIfsc, true),
          gstin: clean(form.gstin, true),
          paymentRequestWhatsapp: form.paymentRequestWhatsapp,
          paymentRequestEmail: form.paymentRequestEmail,
        }),
      })
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['billing-settings'] })
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed'),
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(false)
    save.mutate()
  }

  const bankReady = !!(form.settlementAccountNumber?.trim() && form.settlementIfsc?.trim())

  return (
    <div className="max-w-2xl space-y-8">
      <PageHeader
        title="Fee collection"
        description="Brightstars collects parent fees on your behalf and settles your account at the end of each month. Set how you want to be charged and paid out."
      />

      <Card>
        <CardHeader
          title="Settlement bank account"
          description="Where we transfer your monthly collections. Online payment stays off until these are set."
        />
        <CardBody>
          {!bankReady ? (
            <div className="mb-5 rounded-md border border-warning-border bg-warning-tint px-4 py-3 text-sm text-warning">
              Until you add your bank details, parents can only pay in cash (an
              admin marks the invoice paid). Online checkout is disabled.
            </div>
          ) : null}

          <form onSubmit={onSubmit} className="space-y-4">
            <Field label="Account holder name">
              <Input
                value={form.settlementAccountHolderName ?? ''}
                onChange={(e) =>
                  setForm((f) => ({ ...f, settlementAccountHolderName: e.target.value }))
                }
                placeholder="Brightstars Academy Pvt Ltd"
                autoComplete="off"
              />
            </Field>
            <Field label="Account number" hint="9 to 18 digits">
              <Input
                value={form.settlementAccountNumber ?? ''}
                onChange={(e) =>
                  setForm((f) => ({ ...f, settlementAccountNumber: e.target.value }))
                }
                inputMode="numeric"
                placeholder="000111222333"
                autoComplete="off"
              />
            </Field>
            <Field label="IFSC code">
              <Input
                value={form.settlementIfsc ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, settlementIfsc: e.target.value }))}
                placeholder="HDFC0001234"
                autoComplete="off"
                style={{ textTransform: 'uppercase' }}
              />
            </Field>
            <Field label="GSTIN" hint="Optional. Shown on parent receipts when set.">
              <Input
                value={form.gstin ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, gstin: e.target.value }))}
                placeholder="27ABCDE1234F1Z5"
                autoComplete="off"
                style={{ textTransform: 'uppercase' }}
              />
            </Field>
          </form>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Gateway fee and GST"
          description="A payment gateway fee plus GST applies to every online payment. Choose who pays it."
        />
        <CardBody>
          <Field label="Surcharge">
            <Select
              value={form.surchargeMode}
              onChange={(e) =>
                setForm((f) => ({ ...f, surchargeMode: e.target.value as 'add' | 'absorb' }))
              }
            >
              <option value="add">Add it to the parent's bill (shown as a line item)</option>
              <option value="absorb">Absorb it (deducted from your monthly settlement)</option>
            </Select>
          </Field>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Payment requests"
          description="Channels we use when you send a parent a payment request from the fees page."
        />
        <CardBody>
          <div className="space-y-3">
            <Checkbox
              label="WhatsApp"
              checked={form.paymentRequestWhatsapp}
              onChange={(v) => setForm((f) => ({ ...f, paymentRequestWhatsapp: v }))}
            />
            <Checkbox
              label="Email"
              checked={form.paymentRequestEmail}
              onChange={(v) => setForm((f) => ({ ...f, paymentRequestEmail: v }))}
            />
          </div>

          {error ? (
            <div className="mt-4 rounded-md border border-danger-border bg-danger-tint px-3 py-2 text-sm text-danger">
              {error}
            </div>
          ) : null}
          {success ? (
            <div className="mt-4 rounded-md border border-success-border bg-success-tint px-3 py-2 text-sm text-success">
              Saved.
            </div>
          ) : null}
        </CardBody>
        <CardFooter className="flex items-center justify-end">
          <Button onClick={() => save.mutate()} loading={save.isPending}>
            Save settings
          </Button>
        </CardFooter>
      </Card>
    </div>
  )
}
